import { useCallback, useEffect, useRef, useState } from "react";
import type { Media } from "@atrium/shared";
import { errorMessage, uploadFile, type UploadPurpose } from "../../lib/api";
import { toast } from "../../components/ui/Toast";

export interface PendingUpload {
  key: string;
  file: File;
  preview: string;
  kind: "image" | "video";
  progress: number;
  media: Media | null;
  error: string | null;
}

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

/** Replaces the plain upload, e.g. to encrypt first. Same contract as uploadFile. */
export type Uploader = (file: File, onProgress: (fraction: number) => void, signal: AbortSignal) => Promise<Media>;

/** Upload files as soon as they're picked so posting is instant. */
export function useUploads({ max, allowVideo, purpose = "attachment", upload }: { max: number; allowVideo: boolean; purpose?: UploadPurpose; upload?: Uploader }) {
  const [items, setItems] = useState<PendingUpload[]>([]);
  const controllers = useRef(new Map<string, AbortController>());

  useEffect(
    () => () => {
      controllers.current.forEach((c) => c.abort());
    },
    [],
  );

  const add = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      const accepted: PendingUpload[] = [];
      for (const file of list) {
        const isImage = IMAGE_TYPES.includes(file.type);
        const isVideo = VIDEO_TYPES.includes(file.type);
        if (!isImage && !(allowVideo && isVideo)) {
          toast.error(allowVideo ? "Only images and videos can be attached" : "Only images can be attached");
          continue;
        }
        accepted.push({
          key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          preview: URL.createObjectURL(file),
          kind: isVideo ? "video" : "image",
          progress: 0,
          media: null,
          error: null,
        });
      }
      setItems((cur) => {
        const room = Math.max(0, max - cur.length);
        if (accepted.length > room) toast.error(`You can attach up to ${max} files`);
        const next = accepted.slice(0, room);
        for (const it of next) {
          const ctrl = new AbortController();
          controllers.current.set(it.key, ctrl);
          const onProgress = (p: number) => setItems((all) => all.map((x) => (x.key === it.key ? { ...x, progress: p } : x)));
          (upload ? upload(it.file, onProgress, ctrl.signal) : uploadFile(it.file, purpose, onProgress, ctrl.signal))
            .then((media) => setItems((all) => all.map((x) => (x.key === it.key ? { ...x, media, progress: 1 } : x))))
            .catch((e) => {
              if ((e as Error).name === "AbortError") return;
              setItems((all) => all.map((x) => (x.key === it.key ? { ...x, error: errorMessage(e, "Upload failed") } : x)));
            })
            .finally(() => controllers.current.delete(it.key));
        }
        return [...cur, ...next];
      });
    },
    [allowVideo, max, purpose, upload],
  );

  const remove = useCallback((key: string) => {
    controllers.current.get(key)?.abort();
    setItems((all) => {
      const it = all.find((x) => x.key === key);
      if (it) URL.revokeObjectURL(it.preview);
      return all.filter((x) => x.key !== key);
    });
  }, []);

  const reset = useCallback(() => {
    setItems((all) => {
      all.forEach((x) => URL.revokeObjectURL(x.preview));
      return [];
    });
  }, []);

  const uploading = items.some((i) => !i.media && !i.error);
  const failed = items.some((i) => i.error);
  const mediaIds = items.filter((i) => i.media).map((i) => i.media!.id);
  return { items, add, remove, reset, uploading, failed, mediaIds };
}
